import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import QueryString from 'query-string';
import { Link } from 'react-router-dom';
import { productSearch } from 'api/api';
import { useDispatch, useSelector } from 'react-redux';
import FooterMobile from 'app/layout/footer/FooterMobile';
import Footer from 'app/layout/footer/Footer';
import { Helmet } from 'react-helmet';
import { getSEOFriendlyURL, productCategoryMappingInfo } from 'app/utilities/Utils';
import {
  Button, Col, Container, Row, Spinner,
} from 'react-bootstrap';
import { Placeholder, Svg } from 'app/components/common';
import { Constants } from 'app/utilities';
import CustomScroller from 'app/components/common/CustomScroller';
import { logPageLoad } from 'app/events/Events';
import { productCategoryRequest } from 'app/store/Actions';
import { gaLogPageView, gaLogProductList } from 'ecommerce_ga_events/EcommerceGAEvents';
import { fbLogPageView } from 'facebook/Facebook';
import ProductList from '../global_search/ProductList';
import MoreShopsAround from './layout/MoreShopsAround';
import ReadMoreOnCategories from './layout/ReadMoreOnCategories';
import CategorySeoMetaData from './layout/CategorySeoMetaData';
import './styles/category_landing.scss';
import * as LogClevertapEvent from '../../../clevertap/LogEvent';
import * as screens from '../../events/screens';

function CategoryLanding(props) {
  const { history, location, match } = props;
  const [productsData, setProductsData] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [isError, setError] = useState(false);
  const [isOddCategory, setAsOddCategory] = useState(false);
  const [offset, setOffset] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCat] = useState(null);
  const [filters, setFilters] = useState({
    searchText: '',
    categoryId: '',
    subCategoryId: '',
  });
  const {
    selectedAddress, language, cartItems,
    categories,
  } = useSelector((state) => state.main);
  const dispatch = useDispatch();

  const logOnLoadEvents = (data, resetOffset = false) => {
    if (data.totalCount) {
      let gaProductItems = [];
      data.data.map((store) => {
        const temp = store.products.map((item, index) => ({
          index,
          item_id: item.id,
          item_name: item.displayName,
          affiliation: 'LoveLocal Stores',
          item_variant: item.measurementDisplayText,
          price: item.mrp,
          item_brand: '',
          item_category: '',
        }));
        gaProductItems = gaProductItems.concat(temp);
        return 0;
      });
      gaLogProductList(
        'category_wise_products',
        'Category Products',
        gaProductItems,
      );
    }
    if (resetOffset && filters?.searchText) {
      LogClevertapEvent.searchCategoryLandingPage(
        data.totalCount ? filters?.searchText : null,
        !data.totalCount ? filters?.searchText : null,
      );
    }
    if (resetOffset) {
      logPageLoad(
        {
          SCREEN: screens.CATEGORY_LANDING,
          CATEGORY_ID: selectedCategory.categoryId,
          PRODUCT_COUNT: data.count,
          ...(
            selectedSubCategory?.categoryId ? ({
              SUB_CATEGORY_ID: selectedSubCategory.categoryId,
            }) : {}
          ),
          ...(
            filters?.searchText ? ({
              SEARCH_TEXT: filters.searchText,
            }) : {}
          ),
          ...(
            filters?.storeCode ? ({
              STORE_ID: filters.storeCode,
            }) : {}
          ),
        },
      );
    }
  };

  const loadOnlineProducts = (cats = null, resetOffset = false) => {
    setLoading(true);
    setError(false);
    setAsOddCategory(false);
    const catMeta = ((categories || cats) || []).find((i) => i.categoryId === filters.categoryId);
    const subCat = (catMeta?.children.find((i) => i.categoryId === filters.subCategoryId));
    productSearch(
      'GET',
      '',
      {
        latitude: selectedAddress ? selectedAddress.location.lat : '19.1207983',
        longitude: selectedAddress ? selectedAddress.location.lng : '72.8782323',
        productCategories: subCat?.id || catMeta?.id,
        searchText: filters.searchText,
        limit: 5,
        page: offset,
      },
    ).then((response) => {
      setOffset(offset + 1);
      setLoading(false);
      setSelectedCategory(catMeta);
      setSelectedSubCat(subCat);
      logOnLoadEvents(response.data, resetOffset);
      if (offset === 0 || !productsData) {
        setProductsData(response.data);
      } else {
        setProductsData({
          ...productsData,
          ...response.data,
          data: productsData.data
            .concat(response.data.data),
        });
      }
    }).catch(() => {
      setLoading(false);
      setError(true);
    });
  };

  const loadCategories = () => {
    dispatch(productCategoryRequest(
      null,
      '',
      {
        type: 'async',
        callback: (data, callback) => {
          callback(data);
          loadOnlineProducts(data.res, true);
        },
      },
    ));
  };

  useEffect(() => {
    const queryParams = QueryString.parse(location.search);
    const { q = '' } = queryParams;
    let { categoryId } = match.params;
    let { subCategoryId = null } = queryParams;
    const { pCat = '', sCat = '' } = match.params;
    categoryId = categoryId || pCat.replace(/-/g, '_');
    subCategoryId = subCategoryId || sCat.replace(/-/g, '_') || null;
    if (
      categoryId !== filters.categoryId
      || subCategoryId !== filters.subCategoryId
      || q !== filters.searchText
    ) {
      setOffset(1);
      if (selectedCategory?.categoryId !== categoryId) {
        setSelectedCategory(null);
        setSelectedSubCat(null);
      }
      setProductsData(null);
      setFilters({
        categoryId,
        subCategoryId,
        // offset: 0,
        searchText: q,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, match]);

  // check isoddcategoryflag
  useEffect(() => {
    if (categories && categories.length > 0) {
      const primaryCategory = categories && categories
        .find((category) => category.categoryId === filters.categoryId);
      if (primaryCategory) {
        setAsOddCategory(false);
        const subCat = primaryCategory.children.find(
          (category) => category.categoryId === filters.subCategoryId,
        );
        setSelectedCategory(primaryCategory);
        setSelectedSubCat(subCat);
        loadOnlineProducts(true);
      } else {
        setLoading(false);
        setError(false);
        setAsOddCategory(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  useEffect(() => {
    if (!isLoading) {
      if (categories) {
        loadOnlineProducts(true);
      } else {
        loadCategories();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    fbLogPageView();
    gaLogPageView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // const onChangeSubCategory = () => {
  //   setProductsData(null);
  // };

  const handleLazyLoad = (event) => {
    if (
      !productsData
      || isLoading
      || isError
      || productsData.count === 0
      || !productsData.next
    ) {
      return;
    }
    const {
      offsetHeight, scrollTop, scrollHeight,
    } = event.target;
    if (scrollHeight - offsetHeight - scrollTop < 10
      || offsetHeight + scrollTop === scrollHeight) {
      loadOnlineProducts();
    }
  };

  const pageMeta = (
    <Helmet>
      <title>
        {productCategoryMappingInfo(selectedCategory || {}).seoMetaTitle}
      </title>
      <meta
        name="description"
        content={productCategoryMappingInfo(selectedCategory || {}).seoMetaDescription}
      />
      <link
        rel="canonical"
        href={`https://${window.location.host}/pc${filters.categoryId ? `/${filters.categoryId.replace(/_/g, '-')}` : ''}${filters.subCategoryId ? `/${filters.subCategoryId.replace(/_/g, '-')}` : ''}`}
      />
    </Helmet>
  );

  const loader = (
    <Row
      className="py-3 mx-0 mt-3 w-100 justify-content-center"
    >
      {pageMeta}
      <Spinner
        animation="border"
        variant="primary"
      />
    </Row>
  );

  const errorPlaceholder = (
    <>
      {pageMeta}
      <Placeholder
        language={language}
        handleRetry={() => loadOnlineProducts()}
        imageSrc={productsData ? null : '/images/error-placeholder.png'}
        heading={Constants.String.OH_NO[language]}
      />
    </>
  );

  return (
    <Container
      fluid
      className="h-100 px-0"
    >
      {pageMeta}
      <Row
        className="mx-0 poducts-section d-flex align-items-left bg-white"
        onScroll={!productsData ? null : handleLazyLoad}
      >
        <Col
          xs={24}
          className="px-0"
        >
          <Container className="px-0">
            {
              !!selectedCategory && (
                <Row className="mx-0 py-4">
                  <Col
                    xs={24}
                    className=""
                  >
                    <h1 className="fs-3">
                      {(selectedSubCategory || selectedCategory)?.displayName}
                    </h1>
                  </Col>
                  <Col
                    xs={24}
                    className="d-flex text-gray-light fs-5"
                  >
                    <Link
                      className="text-gray-light"
                      to="/"
                    >
                      Home
                    </Link>
                    &nbsp;&nbsp;/&nbsp;&nbsp;
                    <Link
                      className="text-gray-light"
                      to="/pc"
                    >
                      All
                    </Link>
                    &nbsp;&nbsp;/&nbsp;&nbsp;
                    <span>{selectedCategory?.displayName}</span>
                    &nbsp;&nbsp;/&nbsp;&nbsp;
                    <span>{selectedSubCategory?.displayName}</span>
                  </Col>
                  {/* subcategories listing */}
                  {/* <Col
                    xs={24}
                    className="bg-white px-0 border-bottom"
                  >
                    {
                      selectedCategory && selectedCategory.children.length > 0 ? (
                        <div
                          className="mx-4 d-flex r__flex-10 py-3"
                          style={{ overflowX: 'scroll' }}>
                          {
                            selectedCategory.children.map((subcat) => (
                              <Link
                                to={{
                                  pathname: `/pc/${
                                    getSEOFriendlyURL(filters.categoryId)
                                  }/${
                                    getSEOFriendlyURL(subcat.categoryId)
                                  }${filters.searchText ? `?q=${filters.searchText}` : ''}`,
                                  state: {
                                    scrollToTop: true,
                                  },
                                }}
                                replace={false}
                              >
                                <Button
                                  variant={
                                    subcat.categoryId === filters.subCategoryId
                                    ? 'primary'
                                    : 'outline-primary'
                                  }
                                  onClick={() => {
                                    onChangeSubCategory(subcat.categoryId);
                                  }}
                                  className={`category-button p-1 px-2 fs-4
                                  ${subcat.categoryId !== filters.subCategoryId
                                    ? 'disabled' : 'enabled'}
                                  `}
                                >
                                  {subcat.displayName}
                                </Button>
                              </Link>
                            ))
                          }
                        </div>
                      ) : null
                    }
                  </Col> */}
                </Row>
              )
            }
          </Container>
          {
            productsData && productsData.data && productsData.data.length > 0 && (
              <>
                {
                  productsData.data.map((stores) => (
                    <Container
                      fluid
                      className="bg-white px-0"
                      key={stores.id}
                    >
                      <Container
                        className="px-0 px-lg-2 py-4 border-bottom"
                      >
                        <Row className="mb-2 mx-0">
                          <Col
                            xs={24}
                            className="px-4"
                          >
                            <Row
                              className="d-flex align-items-center pb-3 mx-n4"
                            >
                              <Col
                                className="pl-4 pr-1"
                              >
                                <div
                                  className="fs-4 font-weight-bold"
                                >
                                  {stores.name}
                                </div>
                                <div
                                  className="d-flex align-items-center fs-6 font-weight-bold mb-2"
                                >
                                  <div
                                    className="d-flex align-items-center"
                                  >
                                    <Svg
                                      svg="newStar"
                                      height="16"
                                      width="16"
                                    />
                                    &nbsp;
                                    {(stores.rating > 0 && (stores.rating).toFixed(2)) || '--'}
                                  </div>
                                  &nbsp;&nbsp;·&nbsp;&nbsp;
                                  <div
                                    className="d-flex align-items-center"
                                  >
                                    <Svg
                                      svg="bike"
                                      height="16"
                                      width="16"
                                    />
                                    &nbsp;&nbsp;
                                    {stores.averageDeliveryTimeString || '--'}
                                  </div>
                                  &nbsp;&nbsp;·&nbsp;&nbsp;
                                  <div>
                                    {(stores.calculatedDistance / 1000).toFixed(2) || '--'}
                                    Km
                                  </div>
                                </div>
                              </Col>
                              <Col
                                xs="auto"
                                className="p-0"
                              >
                                <Link
                                  to={`/india/mumbai/andheri/grocers/${getSEOFriendlyURL(stores.name)}-${stores.id}`.toLowerCase()}
                                >
                                  <Button
                                    variant="link"
                                    className="text-dark d-flex align-items-center mx-auto"
                                  >
                                    <span
                                      className="d-flex align-items-center text-primary fs-4"
                                    >
                                      {Constants.String.VISIT_STORE[language]}
                                    </span>
                                      &nbsp;&nbsp;
                                    <span>
                                      <Svg
                                        svg="circleArrow"
                                        circleFill={Constants.Color.primary}
                                        pathFill={Constants.Color.white}
                                        width="1.5rem"
                                      />
                                    </span>
                                  </Button>
                                </Link>
                              </Col>
                            </Row>
                          </Col>
                          <Col
                            xs={24}
                            className="px-4 px-lg-2"
                          >
                            <CustomScroller
                              content={(
                                <div className="d-flex">
                                  {
                                    stores.productsWithMeasurements.map((product) => (
                                      <ProductList
                                        {...props}
                                        key={product[0].retailerProductId}
                                        item={product}
                                        language={language}
                                        cartItems={cartItems}
                                        history={history}
                                        onSuccess={() => {
                                          // this.loadInStoreProductSearch(stores.id);
                                        }}
                                      />
                                    ))
                                  }
                                  {
                                    stores.comboOffers.map((product) => (
                                      <ProductList
                                        {...props}
                                        key={product.retailerProductId}
                                        item={[product]}
                                        language={language}
                                        cartItems={cartItems}
                                        history={history}
                                        onSuccess={() => {
                                          // this.loadInStoreProductSearch(stores.id);
                                        }}
                                        offerProduct
                                      />
                                    ))
                                  }
                                  {
                                    stores.relatedProducts.map((product) => (
                                      <ProductList
                                        {...props}
                                        key={product[0].retailerProductId}
                                        item={product}
                                        language={language}
                                        cartItems={cartItems}
                                        history={history}
                                        onSuccess={() => {
                                          // this.loadInStoreProductSearch(stores.id);
                                        }}
                                        relatedProduct
                                      />
                                    ))
                                  }
                                </div>
                              )}
                              contentPrefix={`searchProduct-${stores.id}`}
                            />
                          </Col>
                        </Row>
                      </Container>
                    </Container>
                  ))
                }
              </>
            )
          }
          {
            ((productsData && productsData.data && productsData.data.length === 0)
            || isOddCategory) && (
              <Placeholder
                language={language}
                imageSrc="/images/no-results.png"
                heading={Constants.String.NO_RESULTS_FOUND[language]}
              />
            )
          }
          {isLoading ? loader : null}
          {isError ? errorPlaceholder : null}
        </Col>
      </Row>
      {/* SEO category meta data */}
      <CategorySeoMetaData selectedCategory={selectedSubCategory || selectedCategory} />
      {/* Read more for seo blog linking */}
      <ReadMoreOnCategories selectedCategoryId={(selectedSubCategory || selectedCategory)?.categoryId || ''} />
      {/* Discover shops around you */}
      <MoreShopsAround />
      <div
        className="mt-6 d-md-none bg-black"
      >
        <FooterMobile
          {...props}
          language={language}
        />
        <div className="p-6" />
      </div>
      <div
        className="mt-6 d-none d-md-block bg-black"
      >
        <Footer
          {...props}
          language={language}
        />
      </div>
    </Container>
  );
}

CategoryLanding.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      categoryId: PropTypes.string,
      pCat: PropTypes.string,
      sCat: PropTypes.string,
    }),
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func,
    push: PropTypes.func,
  }).isRequired,
};

export default CategoryLanding;
